import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  Group,
  Loader,
  Modal,
  Paper,
  Select,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { RichTextEditor } from '@mantine/tiptap';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Color } from '@tiptap/extension-color';
import { TextStyle, FontSize } from '@tiptap/extension-text-style';
import TextAlign from '@tiptap/extension-text-align';
import DOMPurify from 'dompurify';

const MAX_NOTE_HTML_LENGTH = 100_000;

const NOTE_SANITIZE_CONFIG = {
  ALLOWED_TAGS: [
    'p',
    'br',
    'strong',
    'em',
    'u',
    's',
    'h1',
    'h2',
    'h3',
    'h4',
    'ul',
    'ol',
    'li',
    'blockquote',
    'a',
    'span',
    'div',
    'code',
    'pre',
    'hr',
  ],
  ALLOWED_ATTR: ['href', 'target', 'rel', 'style'],
  ALLOW_DATA_ATTR: false,
  KEEP_CONTENT: true,
};

const NOTE_TYPE_OPTIONS = [
  { value: 'all', label: 'All notes' },
  { value: 'session', label: 'Session notes' },
  { value: 'question', label: 'Question notes' },
];

function normalizeString(value) {
  return String(value || '').trim();
}

function firstNonempty(...values) {
  for (const value of values) {
    const cleaned = normalizeString(value);

    if (cleaned) {
      return cleaned;
    }
  }

  return '';
}

function positiveInteger(value) {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}

export function makeNoteSourceKey(noteType, sourceId) {
  return `${noteType}:${sourceId}`;
}

export function getNoteSourceForSession(session, sourceQuery = null) {
  const sourceId = positiveInteger(
    session?.id
      ?? session?.session_id
      ?? session?.session_id_snapshot,
  );

  if (!sourceId) {
    return null;
  }

  const title = firstNonempty(
    session?.title,
    session?.session_title,
    session?.session_title_snapshot,
  ) || `Session ${sourceId}`;

  return {
    noteType: 'session',
    sourceId,
    key: makeNoteSourceKey('session', sourceId),
    title,
    sessionId: sourceId,
    sessionTitle: title,
    sessionDate: firstNonempty(
      session?.session_date,
      session?.session_date_snapshot,
    ),
    sessionType: firstNonempty(
      session?.session_type,
      session?.session_type_snapshot,
    ),
    startSeconds: null,
    endSeconds: null,
    snippet: normalizeString(session?.short_summary),
    sourceQuery: normalizeString(sourceQuery) || null,
  };
}

export function getNoteSourceForQuestion(question, sourceQuery = null) {
  const sourceId = positiveInteger(
    question?.id
      ?? question?.summary_item_id,
  );

  if (!sourceId) {
    return null;
  }

  const sessionId = positiveInteger(
    question?.session_id
      ?? question?.session_id_snapshot,
  );

  const title = firstNonempty(
    question?.title,
    question?.item_text,
    question?.title_snapshot,
  ) || 'Question';

  const sessionTitle = firstNonempty(
    question?.session_title,
    question?.session_title_snapshot,
  );

  return {
    noteType: 'question',
    sourceId,
    key: makeNoteSourceKey('question', sourceId),
    title,
    sessionId,
    sessionTitle,
    sessionDate: firstNonempty(
      question?.session_date,
      question?.session_date_snapshot,
    ),
    sessionType: firstNonempty(
      question?.session_type,
      question?.session_type_snapshot,
    ),
    startSeconds:
      question?.start_seconds !== null && question?.start_seconds !== undefined
        ? Number(question.start_seconds)
        : null,
    endSeconds:
      question?.end_seconds !== null && question?.end_seconds !== undefined
        ? Number(question.end_seconds)
        : null,
    snippet: firstNonempty(
      question?.summary_text,
      question?.snippet_snapshot,
      question?.item_text,
    ),
    sourceQuery: normalizeString(sourceQuery) || null,
  };
}

export function getNoteSourceFromSavedNote(note) {
  if (!note) {
    return null;
  }

  if (note.note_type === 'session') {
    return getNoteSourceForSession({
      id: note.session_id_snapshot,
      title: note.session_title_snapshot || note.title_snapshot,
      session_date: note.session_date_snapshot,
      session_type: note.session_type_snapshot,
      short_summary: note.snippet_snapshot,
    }, note.source_query_snapshot);
  }

  return getNoteSourceForQuestion({
    id: note.summary_item_id || String(note.source_key || '').split(':')[1],
    session_id: note.session_id || note.session_id_snapshot,
    title: note.title_snapshot,
    summary_text: note.snippet_snapshot,
    session_title: note.session_title_snapshot,
    session_date: note.session_date_snapshot,
    session_type: note.session_type_snapshot,
    start_seconds: note.start_seconds,
    end_seconds: note.end_seconds,
  }, note.source_query_snapshot);
}

function sanitizeNoteHtml(html) {
  return DOMPurify.sanitize(String(html || ''), NOTE_SANITIZE_CONFIG).trim();
}

function notePlainText(html) {
  return DOMPurify.sanitize(String(html || ''), {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
    KEEP_CONTENT: true,
  })
    .replace(/\u00a0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function formatTimestamp(seconds) {
  const totalSeconds = Number(seconds);

  if (!Number.isFinite(totalSeconds) || totalSeconds < 0) {
    return '';
  }

  const rounded = Math.floor(totalSeconds);
  const hours = Math.floor(rounded / 3600);
  const minutes = Math.floor((rounded % 3600) / 60);
  const secs = rounded % 60;

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  }

  return `${minutes}:${String(secs).padStart(2, '0')}`;
}

function formatTimeRange(item) {
  const start = formatTimestamp(item?.start_seconds ?? item?.startSeconds);
  const end = formatTimestamp(item?.end_seconds ?? item?.endSeconds);

  if (start && end && start !== end) {
    return `${start}–${end}`;
  }

  return start || end || '';
}

function formatDateTime(value) {
  if (!value) {
    return '';
  }

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return String(value);
  }

  return parsed.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function NoteIcon({ filled = false }) {
  return (
    <svg
      className="note-page-icon"
      viewBox="0 0 24 24"
      width="20"
      height="20"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M6.75 2.75h7.6l3.9 3.9v14.6H6.75z"
        fill={filled ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinejoin="round"
      />
      <path
        d="M14.25 2.9v4h3.85M9.4 11h6.2M9.4 14.3h6.2M9.4 17.6h4.25"
        fill="none"
        stroke={filled ? '#0f172a' : 'currentColor'}
        strokeWidth="1.55"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function NoteButton({
  source,
  status,
  checking = false,
  loading = false,
  onOpen,
}) {
  if (!source) {
    return null;
  }

  const hasNote = Boolean(status?.hasNote);
  const busy = checking || loading;
  const sourceLabel = source.noteType === 'question' ? 'question' : 'session';
  const label = loading
    ? 'Opening note…'
    : checking
      ? 'Checking note status…'
      : hasNote
        ? `Edit the note for this ${sourceLabel}`
        : `Add a note for this ${sourceLabel}`;

  return (
    <Tooltip label={label} withArrow position="top" openDelay={250}>
      <button
        type="button"
        className={`note-toggle-button${hasNote ? ' note-toggle-button-saved' : ''}`}
        aria-label={label}
        disabled={busy}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          onOpen(source);
        }}
      >
        {busy ? <Loader size={16} /> : <NoteIcon filled={hasNote} />}
      </button>
    </Tooltip>
  );
}

function SourceSummary({ source, note }) {
  const timeRange = formatTimeRange(note || source);
  const sessionTitle = normalizeString(
    note?.session_title_snapshot
      ?? source?.sessionTitle,
  );
  const title = normalizeString(
    note?.title_snapshot
      ?? source?.title,
  ) || 'Note';
  const sessionType = normalizeString(
    note?.session_type_snapshot
      ?? source?.sessionType,
  );
  const sessionDate = normalizeString(
    note?.session_date_snapshot
      ?? source?.sessionDate,
  );
  const showSessionTitle = source?.noteType === 'question' && sessionTitle && sessionTitle !== title;

  return (
    <Paper withBorder radius="md" p="sm" className="note-source-summary">
      <Stack gap={6}>
        <Group gap="xs" wrap="wrap">
          <Badge color={source?.noteType === 'question' ? 'teal' : 'blue'} variant="light">
            {source?.noteType === 'question' ? 'Question note' : 'Session note'}
          </Badge>
          {sessionType && <Badge variant="outline">{sessionType}</Badge>}
          {sessionDate && <Badge variant="outline">{sessionDate}</Badge>}
          {timeRange && <Badge variant="outline">{timeRange}</Badge>}
        </Group>

        <Text fw={700} size="lg" className="note-source-title">
          {title}
        </Text>

        {showSessionTitle && (
          <Text size="sm" c="dimmed">
            From: {sessionTitle}
          </Text>
        )}

        {note?.updated_at && (
          <Text size="xs" c="dimmed">
            Last saved {formatDateTime(note.updated_at)}
          </Text>
        )}
      </Stack>
    </Paper>
  );
}

function FontSizeControls({ editor }) {
  const sizes = [
    ['14px', '14'],
    ['18px', '18'],
    ['22px', '22'],
    ['26px', '26'],
  ];

  return (
    <RichTextEditor.ControlsGroup>
      {sizes.map(([fontSize, label]) => (
        <RichTextEditor.Control
          key={fontSize}
          active={editor?.getAttributes('textStyle')?.fontSize === fontSize}
          aria-label={`Set font size to ${label} pixels`}
          title={`${label} px`}
          onClick={() => editor?.chain().focus().setFontSize(fontSize).run()}
        >
          {label}
        </RichTextEditor.Control>
      ))}
      <RichTextEditor.Control
        aria-label="Clear font size"
        title="Clear font size"
        onClick={() => editor?.chain().focus().unsetFontSize().run()}
      >
        A
      </RichTextEditor.Control>
    </RichTextEditor.ControlsGroup>
  );
}

export function NoteEditorModal({
  opened,
  source,
  note,
  loading,
  saving,
  deleting,
  error,
  onClose,
  onSave,
  onDelete,
}) {
  const [html, setHtml] = useState('');
  const [baselineHtml, setBaselineHtml] = useState('');
  const syncTokenRef = useRef(0);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        link: {
          openOnClick: false,
          autolink: true,
          defaultProtocol: 'https',
        },
      }),
      TextStyle,
      Color,
      FontSize,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
    ],
    content: '',
    editable: true,
    onUpdate: ({ editor: updatedEditor }) => {
      setHtml(updatedEditor.getHTML());
    },
  });

  useEffect(() => {
    if (!opened || loading || !editor) {
      return;
    }

    const nextHtml = sanitizeNoteHtml(note?.note_html || '');
    const token = ++syncTokenRef.current;

    editor.commands.setContent(nextHtml || '<p></p>');
    setHtml(nextHtml);
    setBaselineHtml(nextHtml);

    // Keep the final state authoritative even when setContent emits an update.
    const timeoutId = window.setTimeout(() => {
      if (syncTokenRef.current === token) {
        const editorHtml = sanitizeNoteHtml(editor.getHTML());
        setHtml(editorHtml);
        setBaselineHtml(editorHtml);
      }
    }, 0);

    return () => window.clearTimeout(timeoutId);
  }, [opened, loading, note?.id, source?.key, editor]);

  useEffect(() => {
    if (editor) {
      editor.setEditable(!loading && !saving && !deleting);
    }
  }, [editor, loading, saving, deleting]);

  const sanitizedHtml = sanitizeNoteHtml(html);
  const plainText = notePlainText(sanitizedHtml);
  const dirty = sanitizedHtml !== baselineHtml;
  const tooLong = sanitizedHtml.length > MAX_NOTE_HTML_LENGTH;

  function requestClose() {
    if (saving || deleting) {
      return;
    }

    if (dirty) {
      const confirmed = window.confirm('Discard the unsaved changes to this note?');

      if (!confirmed) {
        return;
      }
    }

    onClose();
  }

  function submitNote() {
    if (!plainText || tooLong || saving || deleting) {
      return;
    }

    onSave(sanitizedHtml);
  }

  return (
    <Modal
      opened={opened}
      onClose={requestClose}
      title={source?.noteType === 'question' ? 'Question Note' : 'Session Note'}
      size="xl"
      centered
      zIndex={2000}
      closeOnClickOutside={false}
      closeOnEscape={!saving && !deleting}
      styles={{ body: { maxHeight: 'calc(100vh - 110px)', overflowY: 'auto' } }}
    >
      <Stack gap="md">
        {source && <SourceSummary source={source} note={note} />}

        {loading && (
          <Group justify="center" py="xl">
            <Loader />
          </Group>
        )}

        {error && (
          <Alert color="red" title="Note error">
            {error}
          </Alert>
        )}

        {!loading && editor && (
          <Box className="note-editor-shell">
            <RichTextEditor editor={editor} className="note-rich-text-editor">
              <RichTextEditor.Toolbar sticky stickyOffset={0}>
                <RichTextEditor.ControlsGroup>
                  <RichTextEditor.Bold />
                  <RichTextEditor.Italic />
                  <RichTextEditor.Underline />
                  <RichTextEditor.Strikethrough />
                  <RichTextEditor.ClearFormatting />
                </RichTextEditor.ControlsGroup>

                <RichTextEditor.ControlsGroup>
                  <RichTextEditor.H1 />
                  <RichTextEditor.H2 />
                  <RichTextEditor.H3 />
                  <RichTextEditor.H4 />
                </RichTextEditor.ControlsGroup>

                <FontSizeControls editor={editor} />

                <RichTextEditor.ControlsGroup>
                  <RichTextEditor.ColorPicker
                    colors={[
                      '#f8fafc',
                      '#cbd5e1',
                      '#fa5252',
                      '#e64980',
                      '#be4bdb',
                      '#7950f2',
                      '#4c6ef5',
                      '#228be6',
                      '#15aabf',
                      '#12b886',
                      '#40c057',
                      '#82c91e',
                      '#fab005',
                      '#fd7e14',
                    ]}
                  />
                  <RichTextEditor.UnsetColor />
                </RichTextEditor.ControlsGroup>

                <RichTextEditor.ControlsGroup>
                  <RichTextEditor.AlignLeft />
                  <RichTextEditor.AlignCenter />
                  <RichTextEditor.AlignJustify />
                  <RichTextEditor.AlignRight />
                </RichTextEditor.ControlsGroup>

                <RichTextEditor.ControlsGroup>
                  <RichTextEditor.BulletList />
                  <RichTextEditor.OrderedList />
                  <RichTextEditor.Blockquote />
                </RichTextEditor.ControlsGroup>

                <RichTextEditor.ControlsGroup>
                  <RichTextEditor.Link />
                  <RichTextEditor.Unlink />
                </RichTextEditor.ControlsGroup>

                <RichTextEditor.ControlsGroup>
                  <RichTextEditor.Undo />
                  <RichTextEditor.Redo />
                </RichTextEditor.ControlsGroup>
              </RichTextEditor.Toolbar>

              <RichTextEditor.Content />
            </RichTextEditor>
          </Box>
        )}

        {!loading && (
          <Group justify="space-between" align="center" wrap="wrap">
            <Text size="xs" c={tooLong ? 'red.4' : 'dimmed'}>
              {sanitizedHtml.length.toLocaleString()} / {MAX_NOTE_HTML_LENGTH.toLocaleString()} HTML characters
            </Text>

            <Group gap="xs">
              {note?.id && (
                <Button
                  color="red"
                  variant="subtle"
                  disabled={saving || deleting}
                  onClick={() => onDelete(note)}
                >
                  {deleting ? 'Deleting…' : 'Delete note'}
                </Button>
              )}

              <Button
                variant="default"
                disabled={saving || deleting}
                onClick={requestClose}
              >
                Cancel
              </Button>

              <Button
                disabled={!plainText || tooLong || saving || deleting || !dirty}
                onClick={submitNote}
              >
                {saving ? 'Saving…' : note?.id ? 'Save changes' : 'Save note'}
              </Button>
            </Group>
          </Group>
        )}
      </Stack>
    </Modal>
  );
}

export function canOpenSavedNote(note) {
  if (!note?.live_source_available) {
    return false;
  }

  if (note.note_type === 'session') {
    return positiveInteger(note.session_id) !== null;
  }

  return (
    positiveInteger(note.session_id) !== null
    && note.start_seconds !== null
    && note.start_seconds !== undefined
  );
}

function groupNotesBySession(items) {
  const groups = new Map();

  for (const note of items || []) {
    const sessionKey = positiveInteger(note.session_id_snapshot);

    if (!sessionKey) {
      continue;
    }

    if (!groups.has(sessionKey)) {
      groups.set(sessionKey, {
        sessionIdSnapshot: sessionKey,
        sessionTitle: note.session_title_snapshot || `Session ${sessionKey}`,
        sessionDate: note.session_date_snapshot || '',
        sessionType: note.session_type_snapshot || 'Other',
        liveSessionAvailable: Boolean(note.session_id),
        sessionNote: null,
        questionNotes: [],
      });
    }

    const group = groups.get(sessionKey);

    if (note.note_type === 'session') {
      group.sessionNote = note;
    } else {
      group.questionNotes.push(note);
    }
  }

  return Array.from(groups.values());
}

function NoteLibraryCard({
  note,
  deleting,
  onOpen,
  onEdit,
  onDelete,
}) {
  const timeRange = formatTimeRange(note);
  const canOpen = canOpenSavedNote(note);

  return (
    <Paper withBorder radius="md" p="sm" className="my-note-card">
      <Stack gap="xs">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <div className="my-note-title-block">
            <Group gap="xs" wrap="wrap">
              <Badge color={note.note_type === 'question' ? 'teal' : 'blue'} variant="light">
                {note.note_type === 'question' ? 'Question note' : 'Session note'}
              </Badge>
              {timeRange && <Badge variant="outline">{timeRange}</Badge>}
            </Group>

            <Text fw={700} size="md" mt={5} className="my-note-title">
              {note.title_snapshot || 'Untitled note'}
            </Text>

            <Text size="xs" c="dimmed" mt={2}>
              Updated {formatDateTime(note.updated_at)}
            </Text>
          </div>

          <Group gap="xs" wrap="nowrap">
            <Button
              size="xs"
              variant="light"
              disabled={!canOpen || deleting}
              onClick={() => onOpen(note)}
            >
              {note.note_type === 'session' ? 'Open session' : 'Open transcript'}
            </Button>
            <Button
              size="xs"
              variant="default"
              disabled={deleting}
              onClick={() => onEdit(note)}
            >
              Edit
            </Button>
            <Button
              size="xs"
              variant="subtle"
              color="red"
              disabled={deleting}
              onClick={() => onDelete(note)}
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </Button>
          </Group>
        </Group>

        <Text size="sm" className="my-note-preview" lineClamp={5}>
          {note.note_plain_text}
        </Text>

        {note.snippet_snapshot && (
          <Paper withBorder radius="sm" p="xs" className="my-note-source-snippet">
            <Text size="xs" fw={700} c="dimmed">
              {note.note_type === 'question' ? 'Question snapshot' : 'Session summary snapshot'}
            </Text>
            <Text size="sm" mt={2} lineClamp={3}>
              {note.snippet_snapshot}
            </Text>
          </Paper>
        )}

        {note.source_query_snapshot && (
          <Paper withBorder radius="sm" p="xs" className="my-note-source-query">
            <Text size="xs" fw={700} c="dimmed">Saved from search</Text>
            <Text size="sm" mt={2} lineClamp={2}>
              {note.source_query_snapshot}
            </Text>
          </Paper>
        )}

        {!canOpen && (
          <Text size="xs" c="yellow.4">
            {note.live_source_available
              ? 'The note remains editable, but its source has no transcript timestamp.'
              : 'The original source is no longer available. The note and saved source snapshot remain here.'}
          </Text>
        )}
      </Stack>
    </Paper>
  );
}

export function MyNotesModal({
  opened,
  onClose,
  items,
  total,
  loading,
  loadingMore,
  error,
  deletingId,
  query,
  noteType,
  onQueryChange,
  onNoteTypeChange,
  onClearFilters,
  onOpen,
  onEdit,
  onDelete,
  onLoadMore,
}) {
  const groups = useMemo(() => groupNotesBySession(items), [items]);
  const hasItems = items.length > 0;
  const hasMore = items.length < total;
  const hasFilters = Boolean(normalizeString(query)) || noteType !== 'all';

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="My Notes"
      size="xl"
      centered
      styles={{ body: { maxHeight: 'calc(100vh - 140px)', overflowY: 'auto' } }}
    >
      <Stack gap="md">
        <Paper withBorder radius="md" p="sm" className="my-notes-filter-panel">
          <Group align="end" wrap="wrap">
            <TextInput
              label="Search notes"
              placeholder="Search note text, titles, questions, sessions, or saved queries..."
              value={query}
              maxLength={500}
              onChange={(event) => onQueryChange(event.currentTarget.value)}
              style={{ flex: '1 1 360px' }}
            />

            <Select
              label="Note type"
              data={NOTE_TYPE_OPTIONS}
              value={noteType}
              allowDeselect={false}
              onChange={(value) => onNoteTypeChange(value || 'all')}
              style={{ flex: '0 1 220px' }}
            />

            {hasFilters && (
              <Button
                variant="subtle"
                disabled={loading || loadingMore || deletingId !== null}
                onClick={onClearFilters}
              >
                Clear filters
              </Button>
            )}
          </Group>
        </Paper>

        <Group justify="space-between" align="center" wrap="wrap">
          <Text size="sm" c="dimmed">
            {total === 1 ? '1 note' : `${total} notes`} · grouped by session
          </Text>

          {loading && hasItems && (
            <Group gap="xs">
              <Loader size="xs" />
              <Text size="xs" c="dimmed">Refreshing…</Text>
            </Group>
          )}
        </Group>

        {loading && !hasItems && (
          <Group justify="center" py="xl">
            <Loader />
          </Group>
        )}

        {error && (
          <Alert color="red" title="Notes error">
            {error}
          </Alert>
        )}

        {!loading && !error && !hasItems && (
          <Paper withBorder radius="md" p="lg" className="my-notes-empty">
            <Text fw={700}>
              {hasFilters
                ? 'No notes match the current filters.'
                : 'No notes saved yet.'}
            </Text>
            <Text size="sm" c="dimmed" mt={4}>
              {hasFilters
                ? 'Try another search term or note type.'
                : 'Use the note-page buttons beside sessions and classified questions to create notes.'}
            </Text>
          </Paper>
        )}

        {hasItems && (
          <Stack gap="md">
            {groups.map((group) => (
              <Paper
                key={group.sessionIdSnapshot}
                withBorder
                radius="md"
                p="md"
                className="my-notes-session-group"
              >
                <Stack gap="sm">
                  <Group justify="space-between" align="flex-start" wrap="wrap">
                    <div className="my-notes-session-title-block">
                      <Group gap="xs" wrap="wrap">
                        {group.sessionType && <Badge variant="light">{group.sessionType}</Badge>}
                        {group.sessionDate && <Badge variant="outline">{group.sessionDate}</Badge>}
                        <Badge variant="outline">Session {group.sessionIdSnapshot}</Badge>
                      </Group>
                      <Text fw={700} size="lg" mt={5}>
                        {group.sessionTitle}
                      </Text>
                    </div>
                    <Badge color="cyan" variant="light">
                      {(group.sessionNote ? 1 : 0) + group.questionNotes.length} note{((group.sessionNote ? 1 : 0) + group.questionNotes.length) === 1 ? '' : 's'}
                    </Badge>
                  </Group>

                  {group.sessionNote && (
                    <NoteLibraryCard
                      note={group.sessionNote}
                      deleting={deletingId === group.sessionNote.id}
                      onOpen={onOpen}
                      onEdit={onEdit}
                      onDelete={onDelete}
                    />
                  )}

                  {group.questionNotes.length > 0 && (
                    <Stack gap="xs">
                      <Text size="sm" fw={700} c="dimmed">
                        Question notes
                      </Text>
                      {group.questionNotes.map((note) => (
                        <NoteLibraryCard
                          key={note.id}
                          note={note}
                          deleting={deletingId === note.id}
                          onOpen={onOpen}
                          onEdit={onEdit}
                          onDelete={onDelete}
                        />
                      ))}
                    </Stack>
                  )}
                </Stack>
              </Paper>
            ))}
          </Stack>
        )}

        {hasMore && (
          <Group justify="center">
            <Button
              variant="light"
              disabled={loadingMore || deletingId !== null}
              onClick={onLoadMore}
            >
              {loadingMore ? 'Loading…' : 'Load more'}
            </Button>
          </Group>
        )}
      </Stack>
    </Modal>
  );
}
